import React from 'react';
import isEqual from 'react-fast-compare';

import {updateAndRunAnalysisStep, addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {AnalysisChartViewConfig} from 'analysis-tool/charting/AnalysisChartingTypes';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import getStepVisualizationConfigAndName from 'analysis-tool/data/getVisualizationConfigAndName';
import {getNewStepTypeFromVisualizationConfig} from 'analysis-tool/right-sidebar/utils/saveChartOrTable';
import ChartViewStepEditor from 'analysis-tool/ui/charting/ChartViewStepEditor';
import {getPersistedChartViewConfig} from 'analysis-tool/ui/charting/getPersistedVisualizationConfig';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VisualizationConfig} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {getTableLineageAncestorSteps} from 'pipelines/StepLineageUtils';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisMainViewItemType, MainViewComponentProps} from './MainViewTypes';

function hasStepConfigChanged(
  step: GetAnalysisPipelineStep,
  newViewName: string,
  newChartConfig: AnalysisChartViewConfig
): boolean {
  if (step.definition.stepType === PipelineStepType.CHART_VIEW) {
    const config = getPipelineStepConfig(step, PipelineStepType.CHART_VIEW, 0);

    return config.chartName !== newViewName || !isEqual(config.chartConfig, newChartConfig);
  }

  return getPipelineStepConfig(step, PipelineStepType.TABLE_VIEW, 0).viewName !== newViewName;
}

export default function EditChartView({
  analysis,
  clientStateId,
  onCloseStepInitializationView,
  step: chartOrTableStep,
}: MainViewComponentProps<AnalysisMainViewItemType.EDIT_CHART>): JSX.Element | null {
  const {visualizationConfig: initialVisualizationConfig, viewName: initialViewName} =
    getStepVisualizationConfigAndName(chartOrTableStep);
  const inputDataFrameId = findAndAssertSingleStepDataFrameId(
    chartOrTableStep,
    SINGLE_DATASET_INPUT_STEP_DATA_NAME,
    true
  );
  const viewSteps = getTableLineageAncestorSteps(analysis, chartOrTableStep);

  async function handleSaveChart(visualizationConfig: VisualizationConfig, viewName: string): Promise<void> {
    // if type is table, then replace
    const stepType = getNewStepTypeFromVisualizationConfig(visualizationConfig);
    let newStep;

    if (stepType !== chartOrTableStep.definition.stepType && stepType === PipelineStepType.CHART_VIEW) {
      newStep = await withLoadingScreen(
        addOrReplaceAndRunAnalysisStep(
          analysis.id,
          {
            stepType: PipelineStepType.CHART_VIEW,
            stepVersion: 0,
            stepConfig: {
              chartConfig: getPersistedChartViewConfig(visualizationConfig),
              chartName: viewName,
            },
            stepInputParents: {
              [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
                {
                  stepId: chartOrTableStep.definition.incomingEdges[0].parent.step.id,
                  stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
                },
              ],
            },
            stepInput: {inputDataFrameId},
          },
          {
            originalStepId: chartOrTableStep.id,
          }
        )
      );
    } else if (
      stepType !== chartOrTableStep.definition.stepType &&
      stepType === PipelineStepType.TABLE_VIEW
    ) {
      newStep = await withLoadingScreen(
        addOrReplaceAndRunAnalysisStep(
          analysis.id,
          {
            stepType: PipelineStepType.TABLE_VIEW,
            stepVersion: 0,
            stepConfig: {viewName},
            stepInputParents: {
              [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
                {
                  stepId: chartOrTableStep.definition.incomingEdges[0].parent.step.id,
                  stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
                },
              ],
            },
            stepInput: {inputDataFrameId},
          },
          {
            originalStepId: chartOrTableStep.id,
          }
        )
      );
    } else if (
      !hasStepConfigChanged(chartOrTableStep, viewName, getPersistedChartViewConfig(visualizationConfig))
    ) {
      newStep = chartOrTableStep;
    } else {
      newStep = await withLoadingScreen(
        updateAndRunAnalysisStep(analysis.id, {
          stepId: chartOrTableStep.id,
          stepConfig:
            chartOrTableStep.definition.stepType === PipelineStepType.CHART_VIEW
              ? {
                  chartConfig: getPersistedChartViewConfig(visualizationConfig),
                  chartName: viewName,
                }
              : {
                  viewName,
                },
        }),
        'Updating view...'
      );
    }

    onCloseStepInitializationView({type: AnalysisMainViewItemType.CHART, stepId: newStep.id});
  }

  return (
    <ChartViewStepEditor
      analysis={analysis}
      clientStateId={clientStateId}
      inputDataFrameId={inputDataFrameId}
      handleSaveChart={handleSaveChart}
      viewSteps={viewSteps}
      initialVisualizationConfig={initialVisualizationConfig}
      initialVisualizationName={initialViewName}
    />
  );
}
